// 의견함 판정 한 벌 — 보내기 입력 · 콘솔 목록 거르기 · 처리 입력 · 다음 값 · 장부 한 줄(인메모리·Supabase 가 같이 쓴다).

import {
  FEEDBACK_BODY_MAX,
  FEEDBACK_MEMO_MAX,
  FEEDBACK_PAGE_MAX,
  FEEDBACK_STATUSES,
  FEEDBACK_STATUS_LABEL,
  type FeedbackAuditValues,
  type FeedbackInsert,
  type FeedbackRow,
  type FeedbackStatus,
} from "./types";

type Parsed<T> = { ok: true; value: T } | { ok: false; error: string };

export const FEEDBACK_SENT_MESSAGE = "의견을 보냈습니다. 고맙습니다.";
export const FEEDBACK_EMPTY_MESSAGE = "의견을 적어 주세요";
export const FEEDBACK_TOO_LONG_MESSAGE = `의견은 ${FEEDBACK_BODY_MAX}자까지 적을 수 있습니다`;
// 멤버 한 명이 1분에 보낼 수 있는 의견 수(`0010` 분당 한도 재사용).
export const FEEDBACK_PER_MINUTE = 5;
export const FEEDBACK_RATE_MESSAGE = "의견을 너무 자주 보냈습니다. 1분 뒤 다시 보내 주세요";
export const FEEDBACK_UNAVAILABLE_MESSAGE = "지금은 의견을 보낼 수 없습니다. 잠시 뒤 다시 시도해 주세요";

export const isFeedbackStatus = (v: unknown): v is FeedbackStatus => typeof v === "string" && (FEEDBACK_STATUSES as readonly string[]).includes(v);

export interface FeedbackInput {
  body: string;
  page: string | null;
}

// 보내기 입력 — 본문은 앞뒤 공백을 걷고 1~2000자 · 경로는 `/` 로 시작하는 것만, 길면 잘라 둔다(경로 때문에 의견을 버리지 않는다).
export const parseFeedbackInput = (raw: unknown): Parsed<FeedbackInput> => {
  const input = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const body = typeof input.body === "string" ? input.body.trim() : "";
  if (body === "") return { ok: false, error: FEEDBACK_EMPTY_MESSAGE };
  if (body.length > FEEDBACK_BODY_MAX) return { ok: false, error: FEEDBACK_TOO_LONG_MESSAGE };
  const pageRaw = typeof input.page === "string" ? input.page.trim() : "";
  const page = pageRaw.startsWith("/") ? pageRaw.slice(0, FEEDBACK_PAGE_MAX) : null;
  return { ok: true, value: { body, page } };
};

export const feedbackInsertOf = (input: { id: string; orgId: string; memberId: string; value: FeedbackInput }): FeedbackInsert => ({
  id: input.id,
  org_id: input.orgId,
  member_id: input.memberId,
  body: input.value.body,
  page: input.value.page,
});

// 콘솔 목록 거르기 — null 이면 그 조건 없음.
export interface FeedbackFilter {
  status: FeedbackStatus | null;
  orgId: string | null;
}

// 최신순(같은 시각이면 id 역순) · 조건 AND · offset 부터 limit 개(Supabase 는 같은 뜻을 eq·order·range 로).
export const feedbackListRows = <T extends FeedbackRow>(rows: readonly T[], filter: FeedbackFilter, offset: number, limit: number): T[] =>
  rows
    .filter((r) => (filter.status === null || r.status === filter.status) && (filter.orgId === null || r.org_id === filter.orgId))
    .slice()
    .sort((a, b) => b.created_at.localeCompare(a.created_at) || b.id.localeCompare(a.id))
    .slice(offset, offset + limit);

export const feedbackValuesOf = (row: Pick<FeedbackRow, "status" | "memo" | "status_changed_at">): FeedbackAuditValues => ({
  status: row.status,
  memo: row.memo,
  status_changed_at: row.status_changed_at,
});

export interface FeedbackUpdateInput {
  status: FeedbackStatus;
  memo: string | null;
  // 화면이 본 상태·메모 — 다른 운영자가 먼저 고쳤는지 대조한다.
  before: { status: FeedbackStatus; memo: string | null };
}

const memoOf = (raw: unknown): Parsed<string | null> => {
  if (raw === undefined || raw === null) return { ok: true, value: null };
  if (typeof raw !== "string") return { ok: false, error: "메모는 글자여야 합니다" };
  const trimmed = raw.trim();
  if (trimmed.length > FEEDBACK_MEMO_MAX) return { ok: false, error: `메모는 ${FEEDBACK_MEMO_MAX}자까지입니다` };
  return { ok: true, value: trimmed === "" ? null : trimmed };
};

// 처리 본문 — status · memo(선택) · before{status, memo}.
export const parseFeedbackUpdate = (body: Record<string, unknown> | null): Parsed<FeedbackUpdateInput> => {
  if (!body) return { ok: false, error: "요청 본문이 없습니다" };
  if (!isFeedbackStatus(body.status)) return { ok: false, error: `상태는 ${FEEDBACK_STATUSES.join("·")} 중 하나입니다` };
  const memo = memoOf(body.memo);
  if (!memo.ok) return memo;
  const before = body.before;
  if (!before || typeof before !== "object" || Array.isArray(before)) return { ok: false, error: "before 가 없습니다 — 새로고침한 뒤 다시 해 주세요" };
  const b = before as Record<string, unknown>;
  if (!isFeedbackStatus(b.status) || !(b.memo === null || typeof b.memo === "string")) return { ok: false, error: "before 모양이 틀렸습니다 — 새로고침한 뒤 다시 해 주세요" };
  return { ok: true, value: { status: body.status, memo: memo.value, before: { status: b.status, memo: b.memo as string | null } } };
};

// 화면이 본 값과 지금 값이 같은가 — RPC 가 잠근 뒤 같은 대조를 한 번 더 한다.
export const sameFeedback = (current: Pick<FeedbackRow, "status" | "memo">, before: { status: FeedbackStatus; memo: string | null }): boolean =>
  current.status === before.status && current.memo === before.memo;

// 다음 값 — 상태가 바뀌면 바뀐 시각은 지금 · 메모만 바뀌면 시각을 그대로 · 바뀐 것이 없으면 null.
export const nextFeedbackValues = (current: FeedbackRow, input: Pick<FeedbackUpdateInput, "status" | "memo">, now: Date): FeedbackAuditValues | null => {
  if (current.status === input.status && current.memo === input.memo) return null;
  return {
    status: input.status,
    memo: input.memo,
    status_changed_at: current.status === input.status ? current.status_changed_at : now.toISOString(),
  };
};

// 장부 한 줄 — 「상태 · 메모」.
export const describeFeedback = (values: FeedbackAuditValues | null): string => {
  if (!values) return "—";
  const label = FEEDBACK_STATUS_LABEL[values.status] ?? values.status;
  return values.memo ? `${label} · ${values.memo}` : label;
};
